"use client"

import { DefaultImage } from "@/lib/external-components"
import { getLocalizedHref } from "@/lib/locale-utils"
import { formatDateSimple, generateImagePath } from "@/lib/utils"
import { AllPostsProps } from "@/types/types"
import Image from "next/image"
import Link from "next/link"
import { twMerge } from "tailwind-merge"

const AllPosts = ({
  posts,
  locale = "vi",
  isVideo = false,
  youtubeUrlMap = {},
  onVideoClick,
}: Omit<AllPostsProps, "currentPage" | "totalPages" | "onPageChange" | "categorySlug">) => {
  if (posts.length === 0) return null

  return (
    <div className="flex flex-col gap-0 w-full">
      {posts.map((post) => {
        const blogUrl = getLocalizedHref(post.attributes.locale, `/${post.attributes.slug}`)
        const youtubeUrl = isVideo ? youtubeUrlMap[post.id] : undefined
        const imgAttr =
          post.attributes?.cover?.data?.attributes || post.attributes?.image?.data?.attributes
        const imageUrl = imgAttr?.url
          ? generateImagePath(imgAttr.url, "small", imgAttr.formats)
          : DefaultImage
        const postTitle = post.attributes?.name || post.attributes?.title || ""
        const description = post.attributes?.excerpt || post.attributes?.description || ""
        const publishedAt = post.attributes?.publishedAt || post.attributes?.updatedAt || ""

        const cleanDescription = description ? description.replace(/<[^>]*>/g, "").trim() : ""

        const handleClick = (e: React.MouseEvent) => {
          if (isVideo && youtubeUrl && onVideoClick) {
            e.preventDefault()
            onVideoClick(youtubeUrl)
          }
        }

        return (
          <Link
            key={post.id}
            href={isVideo && youtubeUrl ? "#" : blogUrl}
            onClick={handleClick}
            className={twMerge(
              "group flex sm:flex-row flex-col sm:gap-5 gap-2 sm:py-[26px] py-4 sm:px-2 w-full border-b border-dashed border-public-border-subtle",
              isVideo && youtubeUrl ? "cursor-pointer" : "",
            )}
          >
            <div className="relative sm:w-[27.7%] w-full h-[132px] overflow-hidden flex-shrink-0">
              <Image
                src={imageUrl}
                alt={postTitle}
                fill
                sizes="(max-width: 640px) 100vw, 235px"
                className="object-cover transition-opacity duration-300 group-hover:opacity-90"
                loading="lazy"
                quality={75}
              />
            </div>
            <div className="flex flex-col justify-center sm:w-[72.3%] w-full">
              <h3 className="text-xl font-bold text-public-fg leading-tight group-hover:text-public-brand-strong line-clamp-2 transition-all duration-300">
                {postTitle}
              </h3>
              {!isVideo && cleanDescription && (
                <p className="text-sm text-public-fg leading-relaxed line-clamp-3 sm:pt-2.5 sm:pb-1 pt-[6px]">
                  {cleanDescription}
                </p>
              )}
              {publishedAt && (
                <p className="sm:text-xs text-[10px] sm:leading-[18.6px] leading-[15.5px] text-public-fg font-normal">
                  {formatDateSimple(publishedAt)}
                </p>
              )}
            </div>
          </Link>
        )
      })}
    </div>
  )
}

export default AllPosts